import { authenticateImpersonationToken } from "@/lib/api/impersonation.ts";
import { touchLastActivity } from "@/lib/inactivity";
import { loadStytch } from "@/lib/stytch.ts";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Landing page for Stytch User Impersonation.
 */
export const Route = createFileRoute("/impersonate")({
  component: Impersonate,
});

function Impersonate() {
  const token = useLocation({
    select: (location) => location.state.impersonationToken,
  });
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("Verifying impersonation token…");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!token) {
      setError("No impersonation token was supplied.");
      return;
    }

    const run = async () => {
      try {
        const result = await authenticateImpersonationToken(token);

        setStatus(`Signing in as ${result.user.name}…`);

        const stytch = loadStytch();
        stytch.session.updateSession({
          session_token: result.session_token,
          session_jwt: result.session_jwt,
        });
        await stytch.session.authenticate();

        touchLastActivity();

        window.location.replace("/");
      } catch (e) {
        console.error("Impersonation failed", e);
        setError(
          e instanceof Error
            ? e.message
            : "Could not start the impersonated session.",
        );
      }
    };

    void run();
  }, [token]);

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 p-8 text-center">
      {error ? (
        <>
          <h1 className="text-xl font-semibold text-red-700">
            Impersonation failed
          </h1>
          <p className="max-w-md text-sm text-gray-600">{error}</p>
          <p className="max-w-md text-xs text-gray-500">
            Impersonation tokens are single-use and expire 5 minutes after they
            are created. Generate a new one from the Stytch Dashboard.
          </p>
          <a className="text-sm underline" href="/login">
            Back to login
          </a>
        </>
      ) : (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          <p className="text-sm text-gray-600">{status}</p>
        </>
      )}
    </div>
  );
}
